/**
 * Phone UI - Phone Companion Interface
 *
 * Phone companion page with:
 * - Paste text input with preview
 * - PDF import with cleanup toggle
 * - Remote transport controls (enabled when paired)
 * - WPM slider (live updates to reader)
 * - localStorage persistence for last text and WPM
 *
 * All styling uses tokens.css variables. Cyan accent is focus/active only.
 */

import { PACING_CONFIG } from '../../core/pacing';
import { getSampleText } from '../../content/sampleText';
import { extractPdfText } from '../../ingest/pdfExtract';
import { cleanPdfText, type CleanupResult } from '../../ingest/pdfClean';
import {
  extractSessionCodeFromUrl,
  isSupabaseConfigured,
  createPhonePairing,
  type PairingManager,
  type PairingState,
} from '../../pairing';

/**
 * Create SVG icon element for transport controls (flat style)
 */
function createIcon(name: 'play' | 'pause' | 'rewind' | 'fastForward' | 'restart' | 'skipStart' | 'skipEnd', size = 20): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  const paths: Record<string, string> = {
    play: 'M8 5v14l11-7z',
    pause: 'M6 4h4v16H6zm8 0h4v16h-4z',
    rewind: 'M11 18V6l-8.5 6 8.5 6zm.5-6 8.5 6V6l-8.5 6z',
    fastForward: 'M4 18l8.5-6L4 6v12zm9-12v12l8.5-6-8.5-6z',
    restart: 'M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z',
    skipStart: 'M6 6h2v12H6zm3.5 6 8.5 6V6z',
    skipEnd: 'M6 18l8.5-6L6 6v12zm10-12h2v12h-2z',
  };
  path.setAttribute('d', paths[name]);
  svg.appendChild(path);
  return svg;
}

/**
 * Replace button icon content
 */
function setButtonIcon(btn: HTMLButtonElement, icon: SVGSVGElement): void {
  btn.textContent = '';
  btn.appendChild(icon);
}

/**
 * localStorage keys
 */
const STORAGE_KEYS = {
  LAST_TEXT: 'rsvp-xr-phone-last-text',
  WPM: 'rsvp-xr-phone-wpm',
} as const;

/**
 * Phone UI state
 */
interface PhoneUIState {
  wpm: number;
  isPaired: boolean;
  textToSend: string;
  pdfCleanupEnabled: boolean;
  lastPdfResult: CleanupResult | null;
  sessionCode: string | null;
  pairingManager: PairingManager | null;
  unsubscribePairing: (() => void) | null;
  isPlayingLocally: boolean; // Local tracking for play/pause toggle
}

/**
 * DOM element references
 */
interface PhoneElements {
  container: HTMLElement;
  statusIndicator: HTMLElement;
  textInput: HTMLTextAreaElement;
  clearBtn: HTMLButtonElement;
  sampleTextBtn: HTMLButtonElement;
  sendBtn: HTMLButtonElement;
  pdfImportBtn: HTMLButtonElement;
  pdfFileInput: HTMLInputElement;
  pdfCleanupToggle: HTMLInputElement;
  pdfCleanupLabel: HTMLElement;
  pdfStatus: HTMLElement;
  skipStartBtn: HTMLButtonElement;
  rewindBtn: HTMLButtonElement;
  playPauseBtn: HTMLButtonElement;
  fastForwardBtn: HTMLButtonElement;
  skipEndBtn: HTMLButtonElement;
  wpmSlider: HTMLInputElement;
  wpmValue: HTMLElement;
}

// Module state
let state: PhoneUIState | null = null;
let elements: PhoneElements | null = null;

/**
 * Initialize the Phone UI
 */
export function initPhoneUI(container: HTMLElement): void {
  // Clear container
  container.textContent = '';

  // Load persisted state
  const savedWpm = loadWpm();
  const savedText = loadLastText();

  // Parse session code from URL
  const sessionCode = extractSessionCodeFromUrl(window.location.href);

  // Create UI elements
  const els = createPhoneElements(container);

  // Initialize state
  state = {
    wpm: savedWpm,
    isPaired: false,
    textToSend: savedText,
    pdfCleanupEnabled: true,
    lastPdfResult: null,
    sessionCode,
    pairingManager: null,
    unsubscribePairing: null,
    isPlayingLocally: false,
  };
  elements = els;

  // Set initial values
  els.wpmSlider.value = String(savedWpm);
  els.wpmValue.textContent = String(savedWpm);
  if (savedText) {
    els.textInput.value = savedText;
  }

  // Wire up event handlers
  wireEventHandlers(els);

  // Update UI state (controls disabled until paired)
  updateConnectionStatus(false);

  // Initialize pairing if session code is present and Supabase is configured
  if (sessionCode && isSupabaseConfigured()) {
    initPairing(sessionCode);
  } else if (!sessionCode) {
    els.statusIndicator.textContent = 'No session code';
  } else {
    els.statusIndicator.textContent = 'Pairing unavailable';
  }
}

/**
 * Create all DOM elements for the phone UI
 */
function createPhoneElements(container: HTMLElement): PhoneElements {
  container.className = 'phone-container';

  // Header with title and connection status
  const header = document.createElement('header');
  header.className = 'phone-header';

  // Logo link - clicking returns to landing page
  const logoLink = document.createElement('a');
  logoLink.href = '/';
  logoLink.className = 'logo-link';
  logoLink.setAttribute('aria-label', 'Return to landing page');

  const title = document.createElement('h1');
  title.className = 'phone-title';
  // Split logo: "RSVP" white + "XR" gold
  const brandRsvp = document.createElement('span');
  brandRsvp.className = 'brand-rsvp';
  brandRsvp.textContent = 'RSVP';
  const brandXr = document.createElement('span');
  brandXr.className = 'brand-xr';
  brandXr.textContent = 'XR';
  title.appendChild(brandRsvp);
  title.appendChild(brandXr);
  logoLink.appendChild(title);
  header.appendChild(logoLink);

  const statusIndicator = document.createElement('div');
  statusIndicator.className = 'connection-status';
  statusIndicator.textContent = 'Not connected';
  header.appendChild(statusIndicator);

  container.appendChild(header);

  // Main content area
  const main = document.createElement('main');
  main.className = 'phone-main';

  // Text input section
  const inputSection = document.createElement('section');
  inputSection.className = 'input-section';

  const textInput = document.createElement('textarea');
  textInput.id = 'phone-text-input';
  textInput.className = 'phone-text-input';
  textInput.placeholder = 'Paste or type text here...';
  textInput.rows = 4;
  inputSection.appendChild(textInput);

  // Input actions row
  const inputActions = document.createElement('div');
  inputActions.className = 'input-actions';

  const pdfImportBtn = document.createElement('button');
  pdfImportBtn.type = 'button';
  pdfImportBtn.className = 'action-btn';
  pdfImportBtn.textContent = 'Import PDF';
  pdfImportBtn.title = 'Import text from PDF';
  inputActions.appendChild(pdfImportBtn);

  // Hidden file input for PDF selection
  const pdfFileInput = document.createElement('input');
  pdfFileInput.type = 'file';
  pdfFileInput.accept = '.pdf,application/pdf';
  pdfFileInput.className = 'pdf-file-input';
  pdfFileInput.hidden = true;
  inputActions.appendChild(pdfFileInput);

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'action-btn';
  clearBtn.textContent = 'Clear';
  clearBtn.title = 'Clear text';
  inputActions.appendChild(clearBtn);

  const sampleTextBtn = document.createElement('button');
  sampleTextBtn.type = 'button';
  sampleTextBtn.className = 'action-btn';
  sampleTextBtn.textContent = 'Load Sample';
  sampleTextBtn.title = 'Load sample text for demo';
  inputActions.appendChild(sampleTextBtn);

  inputSection.appendChild(inputActions);

  // PDF cleanup toggle (hidden by default, shown after PDF import)
  const pdfOptionsRow = document.createElement('div');
  pdfOptionsRow.className = 'pdf-options-row';
  pdfOptionsRow.hidden = true;

  const pdfCleanupLabel = document.createElement('label');
  pdfCleanupLabel.className = 'cleanup-toggle-label';

  const pdfCleanupToggle = document.createElement('input');
  pdfCleanupToggle.type = 'checkbox';
  pdfCleanupToggle.checked = true;
  pdfCleanupToggle.className = 'cleanup-toggle';

  const cleanupLabelText = document.createElement('span');
  cleanupLabelText.textContent = 'Clean output (remove headers, etc.)';

  pdfCleanupLabel.appendChild(pdfCleanupToggle);
  pdfCleanupLabel.appendChild(cleanupLabelText);
  pdfOptionsRow.appendChild(pdfCleanupLabel);
  inputSection.appendChild(pdfOptionsRow);

  // PDF status message (for errors/warnings)
  const pdfStatus = document.createElement('div');
  pdfStatus.className = 'pdf-status';
  pdfStatus.hidden = true;
  inputSection.appendChild(pdfStatus);

  main.appendChild(inputSection);

  // Send button
  const sendBtn = document.createElement('button');
  sendBtn.type = 'button';
  sendBtn.className = 'send-btn';
  sendBtn.textContent = 'Send to Reader';
  sendBtn.title = 'Send text to reader (connect first)';
  sendBtn.disabled = true; // Enabled when paired
  main.appendChild(sendBtn);

  container.appendChild(main);

  // Remote controls section (bottom fixed)
  const controlsSection = document.createElement('section');
  controlsSection.className = 'remote-controls';

  const controlsLabel = document.createElement('div');
  controlsLabel.className = 'section-label';
  controlsLabel.textContent = 'Remote Controls';
  controlsSection.appendChild(controlsLabel);

  // Transport controls
  const transport = document.createElement('div');
  transport.className = 'phone-transport';

  const skipStartBtn = createControlButton('skip-start-btn', createIcon('skipStart'), 'Skip to start');
  const rewindBtn = createControlButton('rewind-btn', createIcon('rewind'), 'Rewind 10 words');
  const playPauseBtn = createControlButton('play-pause-btn', createIcon('play', 24), 'Play');
  playPauseBtn.classList.add('primary');
  const fastForwardBtn = createControlButton('ff-btn', createIcon('fastForward'), 'Fast-forward 10 words');
  const skipEndBtn = createControlButton('skip-end-btn', createIcon('skipEnd'), 'Skip to end');

  transport.appendChild(skipStartBtn);
  transport.appendChild(rewindBtn);
  transport.appendChild(playPauseBtn);
  transport.appendChild(fastForwardBtn);
  transport.appendChild(skipEndBtn);

  controlsSection.appendChild(transport);

  // WPM control (matches reader layout: value + label above slider)
  const wpmControl = document.createElement('div');
  wpmControl.className = 'phone-wpm-control';

  const wpmDisplay = document.createElement('div');
  wpmDisplay.className = 'wpm-display';

  const wpmValue = document.createElement('span');
  wpmValue.className = 'wpm-value';
  wpmValue.textContent = String(PACING_CONFIG.WPM_DEFAULT);

  const wpmLabel = document.createElement('span');
  wpmLabel.className = 'wpm-label';
  wpmLabel.textContent = 'WPM';

  wpmDisplay.appendChild(wpmValue);
  wpmDisplay.appendChild(wpmLabel);

  const wpmSlider = document.createElement('input');
  wpmSlider.type = 'range';
  wpmSlider.id = 'phone-wpm-slider';
  wpmSlider.className = 'wpm-slider';
  wpmSlider.min = String(PACING_CONFIG.WPM_MIN);
  wpmSlider.max = String(PACING_CONFIG.WPM_MAX);
  wpmSlider.value = String(PACING_CONFIG.WPM_DEFAULT);
  wpmSlider.step = '25';

  wpmControl.appendChild(wpmDisplay);
  wpmControl.appendChild(wpmSlider);

  controlsSection.appendChild(wpmControl);

  container.appendChild(controlsSection);

  return {
    container,
    statusIndicator,
    textInput,
    clearBtn,
    sampleTextBtn,
    sendBtn,
    pdfImportBtn,
    pdfFileInput,
    pdfCleanupToggle,
    pdfCleanupLabel: pdfOptionsRow,
    pdfStatus,
    skipStartBtn,
    rewindBtn,
    playPauseBtn,
    fastForwardBtn,
    skipEndBtn,
    wpmSlider,
    wpmValue,
  };
}

/**
 * Create a control button element
 */
function createControlButton(id: string, icon: SVGSVGElement, title: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.id = id;
  btn.className = 'control-btn';
  btn.appendChild(icon);
  btn.title = title;
  btn.setAttribute('aria-label', title);
  btn.disabled = true; // Disabled until paired
  return btn;
}

/**
 * Wire up event handlers
 */
function wireEventHandlers(els: PhoneElements): void {
  // Text input - save on change
  els.textInput.addEventListener('input', () => {
    const text = els.textInput.value;
    saveLastText(text);
    if (state) {
      state.textToSend = text;
    }
  });

  // Clear button
  els.clearBtn.addEventListener('click', () => {
    els.textInput.value = '';
    saveLastText('');
    if (state) {
      state.textToSend = '';
    }
  });

  // Sample text button
  els.sampleTextBtn.addEventListener('click', async () => {
    els.sampleTextBtn.disabled = true;
    els.sampleTextBtn.textContent = 'Loading...';
    try {
      const text = await getSampleText();
      els.textInput.value = text;
      saveLastText(text);
      if (state) {
        state.textToSend = text;
      }
    } catch (error) {
      console.error('Failed to load sample text:', error);
    } finally {
      els.sampleTextBtn.disabled = false;
      els.sampleTextBtn.textContent = 'Load Sample';
    }
  });

  // Send button
  els.sendBtn.addEventListener('click', async () => {
    if (!state?.pairingManager?.isConnected) return;

    const text = state.textToSend.trim();
    if (!text) return;

    els.sendBtn.disabled = true;
    els.sendBtn.textContent = 'Sending...';

    try {
      await state.pairingManager.sendText(text);
      // Success feedback handled in event listener
    } catch (err) {
      console.error('Failed to send text:', err);
      els.sendBtn.textContent = 'Send failed';
      setTimeout(() => {
        if (elements) {
          elements.sendBtn.textContent = 'Send to Reader';
          elements.sendBtn.disabled = !state?.isPaired || !state?.textToSend.trim();
        }
      }, 1500);
    } finally {
      els.sendBtn.disabled = !state?.isPaired || !state?.textToSend.trim();
    }
  });

  // WPM slider
  els.wpmSlider.addEventListener('input', () => {
    const wpm = parseInt(els.wpmSlider.value, 10);
    els.wpmValue.textContent = String(wpm);
    saveWpm(wpm);
    if (state) {
      state.wpm = wpm;
      // Send WPM to reader if connected
      if (state.pairingManager?.isConnected) {
        state.pairingManager.sendWpm(wpm).catch((err) => {
          console.error('Failed to send WPM:', err);
        });
      }
    }
  });

  // Transport controls
  els.playPauseBtn.addEventListener('click', async () => {
    if (!state?.pairingManager?.isConnected) return;
    try {
      // Toggle between play and pause based on local tracking
      const command = state.isPlayingLocally ? 'pause' : 'play';
      await state.pairingManager.sendCommand(command);
      // Update local tracking after successful send
      state.isPlayingLocally = !state.isPlayingLocally;
      setButtonIcon(els.playPauseBtn, createIcon(state.isPlayingLocally ? 'pause' : 'play', 24));
      els.playPauseBtn.classList.toggle('playing', state.isPlayingLocally);
    } catch (err) {
      console.error('Failed to send play/pause:', err);
    }
  });

  els.rewindBtn.addEventListener('click', async () => {
    if (!state?.pairingManager?.isConnected) return;
    try {
      await state.pairingManager.sendCommand('rewind', PACING_CONFIG.REWIND_WORDS);
    } catch (err) {
      console.error('Failed to send rewind:', err);
    }
  });

  els.fastForwardBtn.addEventListener('click', async () => {
    if (!state?.pairingManager?.isConnected) return;
    try {
      await state.pairingManager.sendCommand('fastForward', PACING_CONFIG.FAST_FORWARD_WORDS);
    } catch (err) {
      console.error('Failed to send fast-forward:', err);
    }
  });

  els.skipStartBtn.addEventListener('click', async () => {
    if (!state?.pairingManager?.isConnected) return;
    try {
      await state.pairingManager.sendCommand('restart');
      // Reset local play state after skip to start
      state.isPlayingLocally = false;
      if (elements) {
        setButtonIcon(elements.playPauseBtn, createIcon('play', 24));
        elements.playPauseBtn.classList.remove('playing');
      }
    } catch (err) {
      console.error('Failed to send skip-to-start:', err);
    }
  });

  els.skipEndBtn.addEventListener('click', async () => {
    if (!state?.pairingManager?.isConnected) return;
    try {
      // Skip to end - send a large fast-forward to reach the end
      await state.pairingManager.sendCommand('fastForward', 999999);
    } catch (err) {
      console.error('Failed to send skip-to-end:', err);
    }
  });

  // PDF import button - triggers file input
  els.pdfImportBtn.addEventListener('click', () => {
    els.pdfFileInput.click();
  });

  // PDF file input change handler
  els.pdfFileInput.addEventListener('change', async () => {
    const file = els.pdfFileInput.files?.[0];
    if (!file) return;

    // Reset file input so same file can be selected again
    els.pdfFileInput.value = '';

    await handlePdfImport(file, els);
  });

  // PDF cleanup toggle handler
  els.pdfCleanupToggle.addEventListener('change', () => {
    if (!state || !state.lastPdfResult) return;

    state.pdfCleanupEnabled = els.pdfCleanupToggle.checked;
    const text = state.pdfCleanupEnabled
      ? state.lastPdfResult.cleanedText
      : state.lastPdfResult.rawText;

    els.textInput.value = text;
    saveLastText(text);
    state.textToSend = text;
  });
}

/**
 * Initialize pairing connection
 */
async function initPairing(sessionCode: string): Promise<void> {
  if (!state || !elements) return;

  const manager = createPhonePairing(sessionCode);
  state.pairingManager = manager;

  // Subscribe to pairing events
  state.unsubscribePairing = manager.on((event) => {
    switch (event.type) {
      case 'stateChange':
        handlePairingStateChange(event.state);
        break;
      case 'textSent':
        // Text was acknowledged by reader
        if (elements) {
          elements.sendBtn.textContent = 'Sent ✓';
          setTimeout(() => {
            if (elements) elements.sendBtn.textContent = 'Send to Reader';
          }, 1500);
        }
        break;
      case 'commandSent':
        // Command was acknowledged - no UI feedback needed
        break;
      case 'wpmSent':
        // WPM was acknowledged - no UI feedback needed
        break;
      case 'error':
        console.error('Pairing error:', event.error);
        break;
    }
  });

  // Update status to connecting
  if (elements) {
    elements.statusIndicator.textContent = 'Connecting...';
  }

  // Connect to channel
  try {
    await manager.connect();
  } catch (err) {
    console.error('Failed to connect pairing:', err);
    handlePairingStateChange('disconnected');
  }
}

/**
 * Handle pairing state changes
 */
function handlePairingStateChange(pairingState: PairingState): void {
  if (!elements) return;

  switch (pairingState) {
    case 'connecting':
      elements.statusIndicator.textContent = 'Connecting...';
      updateConnectionStatus(false);
      break;
    case 'waiting':
      elements.statusIndicator.textContent = 'Connecting...';
      updateConnectionStatus(false);
      break;
    case 'connected':
      elements.statusIndicator.textContent = 'Connected';
      updateConnectionStatus(true);
      break;
    case 'disconnected':
      elements.statusIndicator.textContent = 'Disconnected';
      updateConnectionStatus(false);
      break;
  }
}

/**
 * Handle PDF file import
 */
async function handlePdfImport(file: File, els: PhoneElements): Promise<void> {
  if (!state) return;

  // Show loading state
  els.pdfImportBtn.disabled = true;
  els.pdfImportBtn.textContent = 'Extracting...';
  els.pdfStatus.hidden = true;
  els.pdfCleanupLabel.hidden = true;

  try {
    // Extract text from PDF
    const extraction = await extractPdfText(file);

    // Clean the text
    const cleanupResult = cleanPdfText(extraction);

    // Check if scanned PDF
    if (cleanupResult.isLikelyScanned) {
      els.pdfStatus.textContent = 'OCR not supported in MVP. This appears to be a scanned or image-only PDF.';
      els.pdfStatus.className = 'pdf-status warning';
      els.pdfStatus.hidden = false;
      return;
    }

    // Store result for toggle
    state.lastPdfResult = cleanupResult;
    state.pdfCleanupEnabled = els.pdfCleanupToggle.checked;

    // Use cleaned or raw text based on toggle
    const text = state.pdfCleanupEnabled ? cleanupResult.cleanedText : cleanupResult.rawText;

    // Update UI
    els.textInput.value = text;
    saveLastText(text);
    state.textToSend = text;

    // Show cleanup toggle
    els.pdfCleanupLabel.hidden = false;

    // Show success message if lines were removed
    if (cleanupResult.removedCount > 0) {
      els.pdfStatus.textContent = `Removed ${cleanupResult.removedCount} header/footer/page number lines. Toggle cleanup to see raw text.`;
      els.pdfStatus.className = 'pdf-status info';
      els.pdfStatus.hidden = false;
    }
  } catch (error) {
    console.error('PDF extraction failed:', error);
    els.pdfStatus.textContent = 'Failed to extract text from PDF. Please try a different file.';
    els.pdfStatus.className = 'pdf-status error';
    els.pdfStatus.hidden = false;
  } finally {
    els.pdfImportBtn.disabled = false;
    els.pdfImportBtn.textContent = 'Import PDF';
  }
}

/**
 * Update connection status UI
 */
function updateConnectionStatus(isPaired: boolean): void {
  if (!elements || !state) return;

  state.isPaired = isPaired;

  // Update status indicator
  elements.statusIndicator.textContent = isPaired ? 'Connected' : 'Not connected';
  elements.statusIndicator.classList.toggle('connected', isPaired);

  // Enable/disable controls based on connection
  elements.sendBtn.disabled = !isPaired || !state.textToSend.trim();
  elements.skipStartBtn.disabled = !isPaired;
  elements.rewindBtn.disabled = !isPaired;
  elements.playPauseBtn.disabled = !isPaired;
  elements.fastForwardBtn.disabled = !isPaired;
  elements.skipEndBtn.disabled = !isPaired;
}

/**
 * Load WPM from localStorage
 */
function loadWpm(): number {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.WPM);
    if (saved) {
      const wpm = parseInt(saved, 10);
      if (wpm >= PACING_CONFIG.WPM_MIN && wpm <= PACING_CONFIG.WPM_MAX) {
        return wpm;
      }
    }
  } catch {
    // localStorage not available
  }
  return PACING_CONFIG.WPM_DEFAULT;
}

/**
 * Save WPM to localStorage
 */
function saveWpm(wpm: number): void {
  try {
    localStorage.setItem(STORAGE_KEYS.WPM, String(wpm));
  } catch {
    // localStorage not available
  }
}

/**
 * Load last text from localStorage
 */
function loadLastText(): string {
  try {
    return localStorage.getItem(STORAGE_KEYS.LAST_TEXT) || '';
  } catch {
    return '';
  }
}

/**
 * Save last text to localStorage
 */
function saveLastText(text: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LAST_TEXT, text);
  } catch {
    // localStorage not available
  }
}

/**
 * Export for pairing integration (Milestone 6)
 */
export function setConnectionStatus(isPaired: boolean): void {
  updateConnectionStatus(isPaired);
}

/**
 * Get current text to send
 */
export function getTextToSend(): string {
  return state?.textToSend || '';
}

/**
 * Get current WPM setting
 */
export function getWpm(): number {
  return state?.wpm || PACING_CONFIG.WPM_DEFAULT;
}

/**
 * Cleanup phone UI
 */
export async function cleanupPhoneUI(): Promise<void> {
  if (state) {
    // Unsubscribe from pairing events
    if (state.unsubscribePairing) {
      state.unsubscribePairing();
    }
    // Disconnect pairing manager
    if (state.pairingManager) {
      await state.pairingManager.disconnect();
    }
  }
  state = null;
  elements = null;
}
